import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas, isRutaRestricted } from '../lib/rutas';
import { CircleCheck as CheckCircle2, Copy, Calendar, Filter, X, DollarSign, User, MapPin, Check, MessageCircle } from 'lucide-react';
import ClientDetailModal from '../components/ClientDetailModal';

interface Contract {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  email: string;
  pix_key: string;
  loan_amount: number;
  total_amount: number;
  status: string;
  payment_status: string;
  paid_at: string | null;
  created_at: string;
  ruta: number;
  seller: {
    email: string;
  } | null;
}

interface FinancialSummary {
  totalLoaned: number;
  totalToReceive: number;
  expectedProfit: number;
  totalReceived?: number;
  realizedProfit?: number;
  lastPaymentDate?: string;
}

interface RutaSummary {
  totalLoaned: number;
  totalToReceive: number;
  expectedProfit: number;
  totalReceived?: number;
  realizedProfit?: number;
}

export default function Financial() {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('pending');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [processingPayment, setProcessingPayment] = useState<string | null>(null);
  const [copiedPixKey, setCopiedPixKey] = useState<string | null>(null);
  const [removingContract, setRemovingContract] = useState<string | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [summary, setSummary] = useState<FinancialSummary>({
    totalLoaned: 0,
    totalToReceive: 0,
    expectedProfit: 0
  });
  const [rutaSummaries, setRutaSummaries] = useState<Record<number, RutaSummary>>({});

  useEffect(() => {
    fetchContracts();
  }, [selectedRuta, paymentStatusFilter, startDate, endDate, user]);

  useEffect(() => {
    if (isRutaRestricted(user)) {
      const visible = getVisibleRutas(user);
      if (!visible.includes(selectedRuta)) setSelectedRuta(visible[0]);
    }
  }, [user]);

  const fetchContracts = async () => {
    if (!user) return;

    try {
      let query = supabase
        .from('contracts')
        .select(`
          *,
          seller:seller_id(email)
        `)
        .eq('approval_status', 'approved')
        .eq('unit_id', user?.unit_id);

      if (selectedRuta > 0) {
        query = query.eq('ruta', selectedRuta);
      }

      if (paymentStatusFilter !== 'all') {
        query = query.eq('payment_status', paymentStatusFilter);
      }

      if (startDate) {
        const dateField = paymentStatusFilter === 'paid' ? 'paid_at' : 'created_at';
        query = query.gte(dateField, startDate);
      }

      if (endDate) {
        const dateField = paymentStatusFilter === 'paid' ? 'paid_at' : 'created_at';
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        query = query.lte(dateField, endDateTime.toISOString());
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      const summaries: Record<number, RutaSummary> = {};
      let totalLoaned = 0;
      let totalToReceive = 0;
      let totalReceived = 0;
      let lastPaymentDate = '';

      (data || []).forEach(contract => {
        if (!summaries[contract.ruta]) {
          summaries[contract.ruta] = {
            totalLoaned: 0,
            totalToReceive: 0,
            expectedProfit: 0,
            totalReceived: 0,
            realizedProfit: 0
          };
        }

        totalLoaned += contract.loan_amount;
        summaries[contract.ruta].totalLoaned += contract.loan_amount;

        if (contract.payment_status === 'paid') {
          totalReceived += contract.total_amount;
          summaries[contract.ruta].totalReceived! += contract.total_amount;

          if (contract.paid_at && (!lastPaymentDate || contract.paid_at > lastPaymentDate)) {
            lastPaymentDate = contract.paid_at;
          }
        } else {
          totalToReceive += contract.total_amount;
          summaries[contract.ruta].totalToReceive += contract.total_amount;
        }
      });

      const totalProfit = totalToReceive - totalLoaned;
      const realizedProfit = totalReceived - (paymentStatusFilter === 'paid' ? totalLoaned : 0);

      Object.keys(summaries).forEach(ruta => {
        const rutaNum = parseInt(ruta);
        summaries[rutaNum].expectedProfit =
          summaries[rutaNum].totalToReceive - summaries[rutaNum].totalLoaned;
        summaries[rutaNum].realizedProfit =
          summaries[rutaNum].totalReceived! - summaries[rutaNum].totalLoaned;
      });

      setSummary({
        totalLoaned,
        totalToReceive,
        expectedProfit: totalProfit,
        totalReceived,
        realizedProfit,
        lastPaymentDate: lastPaymentDate || undefined
      });
      setRutaSummaries(summaries);
      setContracts(data || []);
    } catch (err) {
      console.error('Error fetching contracts:', err);
      setError('Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (contractId: string) => {
    setProcessingPayment(contractId);
    try {
      const { error: contractError } = await supabase
        .from('contracts')
        .update({
          payment_status: 'paid',
          paid_at: new Date().toISOString()
        })
        .eq('id', contractId);

      if (contractError) throw contractError;

      setContracts(contracts.map(contract =>
        contract.id === contractId
          ? {
              ...contract,
              payment_status: 'paid',
              paid_at: new Date().toISOString()
            }
          : contract
      ));

      setRemovingContract(contractId);

      setTimeout(() => {
        setContracts(prev => prev.filter(c => c.id !== contractId));
        setRemovingContract(null);
        fetchContracts();
      }, 3000);
    } catch (err) {
      console.error('Error marking contract as paid:', err);
      setError('Erro ao marcar contrato como pago');
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleCopyPixKey = (pixKey: string) => {
    navigator.clipboard.writeText(pixKey);
    setCopiedPixKey(pixKey);
    setTimeout(() => setCopiedPixKey(null), 2000);
  };

  const clearFilters = () => {
    setSelectedRuta(0);
    setPaymentStatusFilter('pending');
    setStartDate('');
    setEndDate('');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          <p className="text-sm text-gray-500">Carregando contratos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-8">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  const hasActiveFilters = selectedRuta > 0 || startDate || endDate || paymentStatusFilter !== 'pending';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── HEADER ── */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Financeiro</h1>
          <p className="text-sm text-gray-500 mt-0.5">Controle de contratos e pagamentos</p>
        </div>

        {/* ── FILTER TOOLBAR ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Filtros</span>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {/* Status */}
            <div className="flex flex-col gap-1.5 min-w-[140px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
              <select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                className="h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="pending">Pendentes</option>
                <option value="paid">Pagos</option>
                <option value="all">Todos</option>
              </select>
            </div>

            {/* Ruta */}
            <div className="flex flex-col gap-1.5 min-w-[130px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ruta</label>
              <select
                value={selectedRuta}
                onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
                className="h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {!isRutaRestricted(user) && <option value={0}>Todas</option>}
                {getVisibleRutas(user).map(r => <option key={r} value={r}>Ruta {r}</option>)}
              </select>
            </div>

            {/* Data Inicial */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Data Inicial</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-10 pl-9 pr-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Data Final */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Data Final</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-10 pl-9 pr-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Clear */}
            {hasActiveFilters && (
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-transparent select-none">x</label>
                <button
                  onClick={clearFilters}
                  className="h-10 flex items-center gap-1.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl text-sm font-medium transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Limpar
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── RESULT INFO ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>
              <span className="font-bold text-gray-800 text-base">{contracts.length}</span> contrato{contracts.length !== 1 ? 's' : ''} encontrado{contracts.length !== 1 ? 's' : ''}
            </span>
            {paymentStatusFilter === 'pending' && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Pendentes</span>
            )}
            {paymentStatusFilter === 'paid' && (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">Pagos</span>
            )}
          </div>
          {contracts.length > 0 && (
            <div className="text-xs text-gray-400">
              Total: <span className="font-semibold text-gray-600">{formatCurrency(contracts.reduce((s, c) => s + c.total_amount, 0))}</span>
            </div>
          )}
        </div>

        {/* ── CONTRACT LIST ── */}
        {contracts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-16 text-center">
            <DollarSign className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum contrato encontrado</p>
            <p className="text-sm text-gray-400 mt-1">Tente ajustar os filtros aplicados</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contracts.map((contract) => (
              <div
                key={contract.id}
                className={`bg-white rounded-2xl border shadow-sm transition-all duration-500 ${
                  removingContract === contract.id
                    ? 'border-emerald-200 bg-emerald-50 opacity-60 scale-[0.99]'
                    : 'border-gray-100 hover:shadow-md hover:border-gray-200'
                }`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5">

                  {/* ── ACTION BUTTON ── */}
                  {paymentStatusFilter !== 'paid' && (
                    <div className="flex-shrink-0">
                      {contract.payment_status !== 'paid' ? (
                        removingContract === contract.id ? (
                          <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-100 text-emerald-700 rounded-xl">
                            <Check className="w-4 h-4" />
                            <span className="text-sm font-semibold whitespace-nowrap">Pago!</span>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleMarkAsPaid(contract.id)}
                            disabled={processingPayment === contract.id}
                            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap shadow-sm"
                          >
                            <CheckCircle2 className={`w-4 h-4 ${processingPayment === contract.id ? 'animate-spin' : ''}`} />
                            {processingPayment === contract.id ? 'Processando...' : 'Marcar como Pago'}
                          </button>
                        )
                      ) : (
                        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-xl text-xs font-semibold">
                          <Check className="w-3.5 h-3.5" />
                          Pago
                        </span>
                      )}
                    </div>
                  )}

                  {/* ── CLIENT INFO ── */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <button
                          onClick={() => setSelectedContractId(contract.id)}
                          className="text-base font-bold text-gray-900 truncate hover:text-blue-600 transition-colors text-left"
                        >
                          {contract.client_name}
                        </button>
                        {contract.phone && (
                          <a
                            href={`https://wa.me/55${contract.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 mt-0.5 text-xs text-emerald-600 hover:text-emerald-700 transition-colors"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            {contract.phone.replace(/\D/g, '').replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')}
                          </a>
                        )}
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-xs text-gray-500 truncate max-w-[200px]">{contract.pix_key}</span>
                          <button
                            onClick={() => handleCopyPixKey(contract.pix_key)}
                            className="flex-shrink-0 text-blue-500 hover:text-blue-700 transition-colors"
                            title="Copiar chave PIX"
                          >
                            {copiedPixKey === contract.pix_key ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                          {copiedPixKey === contract.pix_key && (
                            <span className="text-xs text-emerald-600 font-medium">Copiado!</span>
                          )}
                        </div>
                      </div>

                      {/* ── VALUES ── */}
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Empréstimo</p>
                          <p className="text-sm font-semibold text-gray-700">{formatCurrency(contract.loan_amount)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Total</p>
                          <p className="text-base font-bold text-gray-900">{formatCurrency(contract.total_amount)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── META ── */}
                  <div className="flex sm:flex-col items-center sm:items-end gap-3 sm:gap-1.5 flex-shrink-0 flex-wrap">
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>
                        {paymentStatusFilter === 'paid' && contract.paid_at
                          ? new Date(contract.paid_at).toLocaleDateString('pt-BR')
                          : new Date(contract.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <User className="w-3.5 h-3.5" />
                      <span className="max-w-[120px] truncate">{contract.seller?.email?.split('@')[0] || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Ruta {contract.ruta}</span>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

      </div>

      {selectedContractId && (
        <ClientDetailModal
          contractId={selectedContractId}
          onClose={() => setSelectedContractId(null)}
        />
      )}
    </div>
  );
}
